import { inject } from '@angular/core';

import {
  type IoConfig,
  type PartialPropsOf,
  type PropsOf,
  type WithElementSearchAndFilters,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import {
  type ElementSizingMode,
  ITEMS_CONTAINER_ACTION_ADD,
  ITEMS_CONTAINER_ACTION_VIEW,
  type PropertiesConfig,
  type PropertiesConfigInputContainerItemActionModel,
  type PropertiesConfigInputOptionsToggle,
  SearchAndFiltersMode,
} from '@blaze-shared/elements-domain';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { COLUMN_ID_OR_NAME, DEFAULT_SCHEMA } from '@blaze-shared/schema-domain';
import { getValueByPath } from '@blaze-shared/workflow-domain';

import { registerElementDefinition } from '../../services/element-definitions.service';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { dragDropPossible, KanbanBoardComponent } from './kanban-board.component';
import { type KanbanBoardPropsModel } from './kanban-board.models';
import { KanbanBoardPropsBuilder } from './kanban-board.props-builder';
import { KanbanBoardPropsExtractor } from './kanban-board.props-extractor';
import { kanbanBoardV2Migration } from './migrations/002-kanban-board.migration';
import { kanbanBoardV3Migration } from './migrations/003-kanban-board.migration';

export type KanbanBoardDef = WithProps<KanbanBoardPropsModel> &
  WithElementSearchAndFilters &
  WithIoConfig;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    kanbanBoard: KanbanBoardDef;
  }
}

const DEFAULT_WIDTH = 600;
const DEFAULT_HEIGHT = 400;
const MIN_SIZE = 300;
const MAX_WIDTH = 900;
const WIDTH_OPTIONS: ElementSizingMode[] = ['fixed', 'full'];

registerElementDefinition({
  type: 'kanbanBoard',
  componentType: KanbanBoardComponent,
  isListed: true,
  name: 'Kanban Board',
  width: { mode: 'full', value: DEFAULT_WIDTH, fixed: true },
  height: { mode: 'value', value: DEFAULT_HEIGHT, fixed: true },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  propsBuilder: KanbanBoardPropsBuilder,
  propsExtractor: KanbanBoardPropsExtractor,
  defaults: getDefaults(),
  initial: getInitial(),
  tags: ['List'],
  styleIncludePaths: ['style', 'content.title.align', 'content.title.iconPosition'],
  metaUiFeatures: {
    type: 'container',
    paths: {
      columns: 'content.customFields',
    },
  },
  features: {
    loadingIndication: 'fadeWithIndicator',
    visualStyle: 'block',
  },
  version: 3,
  migrations: {
    2: kanbanBoardV2Migration,
    3: kanbanBoardV3Migration,
  },
});

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'value',
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        bindingDefs: [],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [{ eventType: 'action', schema: DEFAULT_SCHEMA }],
  };
}

function getDefaults(): PropsOf<'kanbanBoard'> {
  return {
    content: {
      columns: [],
      fields: {},
      customFields: [],
      actions: {
        align: 'left',
        items: [],
        mode: 'always',
      },
      searchAndFilters: {
        mode: SearchAndFiltersMode.PREDEFINED,
        filters: [],
      },
      board: {
        columnWidth: '240px',
      },
      itemAdd: { enabled: false, label: '' },
      itemView: { enabled: false },
      itemEdit: { enabled: false },
      itemDelete: { enabled: false },
      itemDragDrop: { enabled: false },
      title: {
        label: 'Kanban Board',
        align: 'left',
        iconColor: { color1: '$t.title', selectorType: 'solid' },
      },
    },
    style: {
      fontSize: '$t.fontSize',
      fontFamily: '$t.fontFamily',
      lineHeight: '$t.lineHeight',
      general: {
        heading: { color1: '$t.title', selectorType: 'solid' },
        accent: { color1: '$t.accent', selectorType: 'solid' },
        offset: { margin: '$t.spacingExternal', padding: '$t.spacingInternal' },
      },
      titleBlock: {
        fontStyle: '$t.headerFontStyle' as any,
        fontSize: '$t.headerFontSize',
        fontFamily: '$t.headerFontFamily',
        fontWeight: '$t.headerFontWeight',
        iconSize: '$t.headerIconSize',
      },
      header: {
        palette: {
          paletteName: 'palette2',
          foreground: '$t.value',
          background: '$t.outline',
        },
      },
      card: {
        title: { color1: '$t.title', selectorType: 'solid' },
        description: { color1: '$t.value', selectorType: 'solid' },
        actions: { color1: '$t.value', selectorType: 'solid' },
        fill: { color1: '$t.fillInternal', selectorType: 'solid' },
        outline: { color1: '$t.outline', selectorType: 'solid' },
        radius: '$t.radius',
      },
      container: {
        outline: { color1: '$t.outline', selectorType: '$t.containerOutlineType' as any },
        outlineWidth: '$t.containerOutlineWidth',
      },
      background: {
        fill: { color1: '$t.fillInternal', selectorType: 'solid' },
        outline: { color1: '$t.outline', selectorType: 'solid' },
        outlineWidth: '1px',
        radius: '$t.radius',
        visualEffect: {
          type: '$t.effectType',
          data: {
            x: '$t.effectX',
            y: '$t.effectY',
            blur: '$t.effectBlur',
            color1: '$t.effectColor1',
          },
        },
      },
    },
  };
}

function getInitial(): PartialPropsOf<'kanbanBoard'> {
  return {
    content: {
      searchAndFilters: { injectToElement: true },
    },
    style: {
      card: {
        // TODO: radius should be loaded on style reset
        radius: '$t.radius', // to prevent upgrading new instances
      },
    },
    featuresConfig: {
      fieldsLabelGen: 2,
    },
  };
}

function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('kanbanBoard', [
    epc.section('content', 'Content', [
      epc.subsections.dataSubsection(
        'data.?key:value.enabled',
        'Fields',
        [],
        [
          epc.fields.selectDataSourceField({
            key: 'value',
            label: 'Data source',
            sources: 'list',
            bindings: [],
          }),
          ...epc.utils.disabledFields(
            [
              epc.fields.listFieldsField('content.columns', 'data.?key:value.dataSource'),
              epc.fields.selectFieldFromModel(
                'content.fields.columnTitle',
                'Column title',
                'content.columns',
                {
                  showSelection: true,
                  allowNone: true,
                  labelField: 'name',
                  valueField: COLUMN_ID_OR_NAME,
                  optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                },
              ),
              epc.fields.selectFieldFromModel(
                'content.fields.cardTitle',
                'Card title',
                'content.columns',
                {
                  showSelection: true,
                  allowNone: true,
                  labelField: 'name',
                  valueField: COLUMN_ID_OR_NAME,
                  optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                },
              ),
              epc.fields.selectFieldFromModel(
                'content.fields.cardDescription',
                'Card description',
                'content.columns',
                {
                  showSelection: true,
                  allowNone: true,
                  labelField: 'name',
                  valueField: COLUMN_ID_OR_NAME,
                  optionsFilter: epc.utils.columnFormatFilter('!secureText'),
                },
              ),
              epc.utils.separatedField(
                epc.fields.listCustomFieldsField('content.customFields', 'Add Custom Field'),
              ),
            ],
            (model) =>
              !getValueByPath(model, 'data.?key:value.dataSource')
                ? 'The Value of the element is not bound'
                : undefined,
          ),
        ],
        {
          enabledByDefault: true,
          subsectionOptions: { labelWidth: '40%' },
        },
      ),
      epc.subsections.subsection('content.board', 'List options', [
        epc.fields.sliderField('content.board.columnWidth', 'Column width', [
          {
            min: 100,
            max: 300,
            unit: 'px',
          },
        ]),
      ]),
      epc.subsections.searchAndFiltersSubsection(),
      epc.subsections.subsection(
        'content.actions',
        'Actions',
        [
          epc.utils.adornField<'toggle', PropertiesConfigInputOptionsToggle>(
            epc.fields.toggleField('content.itemView.enabled', 'Details on Click'),
            {
              action: () => ({ id: ITEMS_CONTAINER_ACTION_VIEW }),
              conditionsPath: 'content.itemView.visibilityConditions',
              conditionsExpressionPath: 'content.itemView.visibilityExpression',
              conditionsConfig: {
                title: 'Details on click visibility',
                scope: 'component',
              },
            },
          ),
          epc.utils.adornField<'toggle', PropertiesConfigInputOptionsToggle>(
            epc.fields.toggleField('content.itemAdd.enabled', 'Add new item'),
            {
              action: () => ({
                id: ITEMS_CONTAINER_ACTION_ADD,
                styled: { enabled: true, elementType: 'button' },
              }),
              conditionsPath: 'content.itemAdd.visibilityConditions',
              conditionsExpressionPath: 'content.itemAdd.visibilityExpression',
              conditionsConfig: {
                title: 'Add new item visibility',
                scope: 'component',
              },
            },
          ),
          epc.utils.conditionalField(
            epc.fields.textInput('content.itemAdd.label', 'Add label'),
            'content.itemAdd.enabled',
            (value) => !value,
          ),
          epc.utils.conditionalField(
            epc.fields.toggleField('content.itemDragDrop.enabled', 'Drag & Drop between columns', {
              workflowId: (model: PropsOf<'kanbanBoard'>) => model.content.itemDragDrop.workflowId,
              conditionsPath: 'content.itemDragDrop.conditions',
              conditionsExpressionPath: 'content.itemDragDrop.expression',
              conditionsConfig: {
                title: 'Drag & Drop visibility',
                scope: 'component',
              },
            }),
            '',
            (_, model) => !dragDropPossible(model, inject(SchemaService)),
          ),
          epc.utils.conditionalField(
            epc.utils.disabledField(
              epc.fields.toggleField(
                'content.itemDragDrop.__alwaysDisabled',
                'Drag & Drop between columns',
              ),
              () => 'Drag & Drop requires the "Column title" field to be editable.',
            ),
            '',
            (_, model) => dragDropPossible(model, inject(SchemaService)),
          ),
          epc.utils.separatedField(
            epc.fields.listItemActionsField('content.actions.items', 'Additional Actions'),
          ),
          ...epc.utils.conditionalFields(
            [
              epc.utils.separatedField(
                epc.fields.contentAlignField('content.actions.align', 'Align', { hideLabel: true }),
                { label: 'Show Actions', hideSeparatorWhenHidden: true },
              ),
              epc.fields.actionsDisplayModeField('content.actions.mode', 'Display mode', {
                hideLabel: true,
              }),
            ],
            'content.actions.items',
            (actions: PropertiesConfigInputContainerItemActionModel[]) =>
              !actions?.length || actions.every((item) => !(item.label || item.icon)),
          ),
        ],
        {
          lockWhen: (model) =>
            !getValueByPath(model, 'data.?key:value.dataSource')
              ? 'The Value of the element is not bound'
              : undefined,
        },
      ),
      epc.subsections.subsection('content.container', 'Size', [
        epc.root.elementWidthField(
          { min: MIN_SIZE, max: MAX_WIDTH, unit: 'px', step: 1 },
          WIDTH_OPTIONS,
        ),
      ]),
      epc.subsections.visibilitySubsection(),
      epc.subsections.titleContentSubsection('content'),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('style.general', 'Padding & Margin', [
          epc.fields.spacingAndAlignField('style.general.offset', {
            hasMargin: true,
            hasPadding: true,
          }),
        ]),
        epc.subsections.subsection('style.header', 'Header', [
          epc.fields.tagsColorPaletteField('style.header.palette', '', {
            bindingKey: 'value',
            dataSourceKey: 'content.fields.columnTitle',
            hideLabel: true,
          }),
        ]),
        epc.subsections.subsection('style.card', 'Card', [
          epc.fields.colorField('style.card.title', 'Title', {
            allowNone: false,
            allowGradient: false,
          }),
          epc.fields.colorField('style.card.description', 'Description', {
            allowNone: false,
            allowGradient: false,
          }),
          epc.fields.colorField('style.card.actions', 'Actions', {
            allowNone: false,
            allowGradient: false,
          }),
          epc.fields.colorField('style.card.fill', 'Fill', {
            allowNone: false,
            allowGradient: true,
          }),
          epc.fields.colorField('style.card.outline', 'Outline', {
            allowNone: true,
            allowGradient: false,
          }),
          epc.fields.radiusField('style.card.radius'),
        ]),
        epc.subsections.elementBackgroundSubsection('style.background', 'Background'),
        epc.subsections.titleStyleSubsection({
          enabled: 'content.title.enabled',
          color: 'style.general.heading',
          fontFamily: 'style.titleBlock.fontFamily',
          fontWeight: 'style.titleBlock.fontWeight',
          fontStyle: 'style.titleBlock.fontStyle',
          fontSize: 'style.titleBlock.fontSize',
          horizontalAlign: 'content.title.align',
          icon: 'content.title.icon',
          iconColor: 'content.title.iconColor',
          iconPosition: 'content.title.iconPosition',
          iconSize: 'style.titleBlock.iconSize',
        }),
      ],
      true,
    ),
  ]);
}
